import * as React from 'react';

import styled, { ThemeContext } from 'styled-components';
import { formatImgUrl, ImgUrlProps } from 'jamespot-user-api';

/**
 * @see ImgUrlProps
 * @property loading - img loading attribute - default is lazy
 * @property alt - img alt attributes
 *
 * @property type - type of the record image (ex: user, fileArticle, spot, diffgroup, etc.)
 * @property recordId - id of the record
 * @property uri - uri of the record
 *
 * @property width - Width of the <img /> tag. Also used to compute the image size downloaded
 * @property height - Height of the <img /> tag. Also used to compute the image size downloaded
 * @property size - if the url is not provided, used to download the image at this size.
 *                  If not provided, the size will be computed from width and height.
 *                  /!\ This property should not be used unless the image should not be displayed
 *                  at retina resolution for any reason.
 * @property maxWidth - maxWidth (ex: 100%) adapt the image to parent size
 *                  /!\ This property should be used only if parent has a responsive size
 *
 * @property format - format of the image (ex: png, jpeg, etc.) - default is png
 *
 * @property url - img url used in place of width, height, size, uri, recordId and type to download an image
 * @property iconColor - Toggle default icon color white/black
 * @property timestamp - date used to force an image to be downloaded from the server instead
 *                       of from the cache. The modified timestamp of the object being displayed
 *                       should be passed
 *
 * @property className - className of the <img /> tag
 */
export type JRCImgProps = ImgUrlProps & {
    loading?: 'eager' | 'lazy';
    alt: string;
    className?: string;
    onError?: () => void;
    onLoad?: () => void;
};

const Img = styled.img<{
    maxWidth?: string;
    maxHeight?: string;
    aspectRatio?: number;
    objectFit?: string;
}>`
    box-sizing: border-box;
    flex-shrink: 0;
    border: 0;
    max-width: ${(props) => props.maxWidth};
    max-height: ${(props) => props.maxHeight};
    line-height: ${(props) => props.height}px;
    ${(props) => (props.aspectRatio ? `aspect-ratio: ${props.aspectRatio};` : '')}
    ${(props) => (props.objectFit ? `object-fit: ${props.objectFit};` : '')}
`;

export const JRCImg = (props: JRCImgProps) => {
    const themeContext = React.useContext(ThemeContext);

    function onError({ currentTarget }: React.SyntheticEvent<HTMLImageElement, Event>) {
        if (currentTarget && props.fallbackUrl) {
            // prevents looping
            currentTarget.onerror = null;
            currentTarget.src = props.fallbackUrl;
        }
        props.onError?.();
    }

    return (
        <Img
            src={formatImgUrl(props, themeContext.dpr)}
            onError={props.fallbackUrl || props.onError ? onError : undefined}
            onLoad={props.onLoad}
            loading={props.loading || 'lazy'}
            alt={props.alt}
            width={props.width}
            height={props.height}
            maxWidth={props.maxWidth}
            maxHeight={props.maxHeight}
            className={props.className}
            aspectRatio={props.aspectRatio}
            objectFit={props.objectFit}
        />
    );
};
